import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import formStyles from 'pages/Form.module.css';
import styles from 'pages/EditEvent.module.css';
import globalStyles from 'Global.module.css';
import { CreateEventDocument, UpdateEventDocument, CreateImageUploadDocument } from 'graphql/mutations.generated';
import { GetLeadersDocument, GetEditEvenDetailDocument } from 'graphql/queries.generated';
import ImageUpload from 'components/ImageUpload';
import { ImageOwnerType, ImageSize } from 'app-constants';
import Image from 'components/Image';

const EditEvent: React.FC = () => {
    const { organizationId, eventId } = useParams<{ organizationId?: string; eventId?: string }>();
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
  const [tags, setTags] = useState('');
  const [showAttendeeLimit, setShowAttendeeLimit] = useState(false);
  const [attendeeLimit, setAttendeeLimit] = useState('20');
  const [showMaxGuests, setShowMaxGuests] = useState(false);
  const [maxGuests, setMaxGuests] = useState('1');
  const [selectedHosts, setSelectedHosts] = useState<string[]>([]);
    const [initialHosts, setInitialHosts] = useState<string[]>([]);
  const [error, setError] = useState('');
    const [imageId, setImageId] = useState<string | null>(null);
    const [imageUploadData, setImageUploadData] = useState<{ uploadInput: any; file: File } | null>(null);
  const navigate = useNavigate();

    const { data: eventData, loading: eventLoading } = useQuery(GetEditEvenDetailDocument, {
        variables: { eventId: eventId || '' },
        skip: !eventId,
    });

    const actualOrganizationId = organizationId || eventData?.event?.organizationId || '';

  const { data: organizersData, loading: organizersLoading } = useQuery(GetLeadersDocument, {
      variables: { organizationId: actualOrganizationId || '' },
      skip: !actualOrganizationId,
  });

    const [createEvent, { loading: createLoading }] = useMutation(CreateEventDocument);
    const [updateEvent, { loading: updateLoading }] = useMutation(UpdateEventDocument);
    const [createImageUpload] = useMutation(CreateImageUploadDocument);

    const loading = createLoading || updateLoading;

    const convertToLocalDatetime = (isoString: string): string => {
        const date = new Date(isoString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    useEffect(() => {
        if (eventData?.event) {
            const event = eventData.event;
            setTitle(event.title);
            setLocation(event.location);
            setDescription(event.description);
            setStartTime(convertToLocalDatetime(event.startTime));
            setEndTime(convertToLocalDatetime(event.endTime));
            setImageId(event.imageId || null);
            setTags(event.tags.filter(tag => tag).join(', '));

            if (event.attendeeLimit) {
                setShowAttendeeLimit(true);
                setAttendeeLimit(event.attendeeLimit.toString());
            }

            if (event.maxGuests) {
                setShowMaxGuests(true);
                setMaxGuests(event.maxGuests.toString());
            }

            const hostIds = event.hosts.map(host => host.organizationMemberId);
            setSelectedHosts(hostIds);
            setInitialHosts(hostIds);
        }
    }, [eventData]);

  const handleHostToggle = (organizationMemberId: string) => {
    setSelectedHosts((prev) =>
      prev.includes(organizationMemberId)
        ? prev.filter((id) => id !== organizationMemberId)
        : [...prev, organizationMemberId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

      if (!organizationId && !eventData?.event?.organizationId) {
      setError('No organization ID provided');
      return;
    }

    if (selectedHosts.length === 0) {
      setError('Please select at least one host');
      return;
    }

    if (showAttendeeLimit && attendeeLimit) {
      const limit = parseInt(attendeeLimit, 10);
      if (isNaN(limit) || limit <= 0) {
        setError('Attendee limit must be a positive number');
        return;
      }
    }

    if (showMaxGuests && maxGuests) {
      const guests = parseInt(maxGuests, 10);
      if (isNaN(guests) || guests <= 0) {
        setError('Max guests must be a positive number');
        return;
      }
    }

    const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '');

    try {
        let finalImageId = imageId;

        if (imageUploadData) {
            const uploadResult = await createImageUpload({
                variables: imageUploadData.uploadInput,
            });

            if (uploadResult.data?.createImageUpload) {
                finalImageId = uploadResult.data.createImageUpload.imageId;
                const uploadURL = uploadResult.data.createImageUpload.uploadURL;

                await fetch(uploadURL, {
                    method: 'PUT',
                    body: imageUploadData.file,
                    headers: {
                        'Content-Type': imageUploadData.file.type,
                    },
                });

                setImageId(finalImageId);
                setImageUploadData(null);
            }
        }

        if (eventId) {
            const deletedHosts = initialHosts.filter(hostId => !selectedHosts.includes(hostId));

            const result = await updateEvent({
                variables: {
                    eventId,
                    title,
                    location,
                    description,
                    startTime,
                    endTime,
                    tags: tagsArray,
                    attendeeLimit: showAttendeeLimit && attendeeLimit ? parseInt(attendeeLimit, 10) : undefined,
                    maxGuests: showMaxGuests && maxGuests ? parseInt(maxGuests, 10) : undefined,
                    hosts: selectedHosts,
                    deletedHosts,
                    imageId: finalImageId || '',
                },
            });

            if (result.data?.updateEvent) {
                navigate(`/events/${result.data.updateEvent}`);
            }
        } else {
            const result = await createEvent({
                variables: {
                    organizationId: organizationId!,
                    title,
                    location,
                    description,
                    startTime,
                    endTime,
                    tags: tagsArray,
                    attendeeLimit: showAttendeeLimit && attendeeLimit ? parseInt(attendeeLimit, 10) : undefined,
                    maxGuests: showMaxGuests && maxGuests ? parseInt(maxGuests, 10) : undefined,
                    hosts: selectedHosts,
                    imageId: finalImageId || '',
                },
            });

            if (result.data?.createEvent) {
                navigate(`/events/${result.data.createEvent}`);
            }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

    if (eventLoading || organizersLoading) {
    return (
      <div className={formStyles.formContainer}>
                <h1>{eventId ? 'Edit Event' : 'Create Event'}</h1>
                <p>Loading...</p>
            </div>
        );
    }

    if (!organizationId && !eventData?.event?.organizationId) {
        return (
            <div className={formStyles.formContainer}>
                <h1>{eventId ? 'Edit Event' : 'Create Event'}</h1>
        <p className={formStyles.error}>No organization ID provided</p>
      </div>
    );
  }

  return (
    <div className={`${formStyles.formContainer} ${styles.createEvent}`}>
          <h1>{eventId ? 'Edit Event' : 'Create Event'}</h1>
      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="title">Title:</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="location">Location:</label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            required
          />
        </div>
        <div>
          <label htmlFor="startTime">Start Time:</label>
          <input
            type="datetime-local"
            id="startTime"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="endTime">End Time:</label>
          <input
            type="datetime-local"
            id="endTime"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
          />
        </div>
        <div>
                  <label>Event Image:</label>
                  <ImageUpload
                      ownerType={ImageOwnerType.Organization}
                      displaySize={ImageSize.Card}
                      imageId={imageId}
                      ownerId={actualOrganizationId}
                      onImageChange={setImageUploadData}
                      aspectRatio={16 / 9}
                  />
        </div>
        <div>
          <label htmlFor="tags">Tags (comma-separated):</label>
          <input
            type="text"
            id="tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., social, networking, outdoor"
            required
          />
        </div>
        <div className={styles.toggleSection}>
          <label>
            <input
              type="checkbox"
              checked={showAttendeeLimit}
              onChange={(e) => setShowAttendeeLimit(e.target.checked)}
            />
            Limit Attendees
          </label>
          {showAttendeeLimit && (
            <input
              type="number"
              value={attendeeLimit}
              onChange={(e) => setAttendeeLimit(e.target.value)}
              placeholder="Enter attendee limit"
              min="1"
              className={styles.toggleInput}
            />
          )}
        </div>
        <div className={styles.toggleSection}>
          <label>
            <input
              type="checkbox"
              checked={showMaxGuests}
              onChange={(e) => setShowMaxGuests(e.target.checked)}
            />
            Allow Guests
          </label>
          {showMaxGuests && (
            <input
              type="number"
              value={maxGuests}
              onChange={(e) => setMaxGuests(e.target.value)}
              placeholder="Max guests per attendee"
              min="1"
              className={styles.toggleInput}
            />
          )}
        </div>
        <div>
          <label>Hosts: *</label>
          {organizersLoading ? (
            <p>Loading organizers...</p>
          ) : (
            <div className={styles.hostPicker}>
              {organizersData?.leaders.map((organizer) => (
                <div
                  key={organizer.organizationMemberId}
                  className={`${styles.hostOption} ${
                    selectedHosts.includes(organizer.organizationMemberId) ? styles.selected : ''
                  }`}
                  onClick={() => handleHostToggle(organizer.organizationMemberId)}
                >
                      <Image imageOwnerType={ImageOwnerType.Person} imageSize={ImageSize.Thumbnail} imageId={organizer.combinedProfile.imageId} className={globalStyles.profileThumbnail} />
                      <span className={styles.hostUsername}>{organizer.combinedProfile.username}</span>
                  {selectedHosts.includes(organizer.organizationMemberId) && (
                    <span className={styles.checkmark}>✓</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {error && <div className={formStyles.error}>{error}</div>}
              <button type="submit" disabled={loading || eventLoading} className={globalStyles.submitButton}>
                  {loading ? (eventId ? 'Updating event...' : 'Creating event...') : (eventId ? 'Update Event' : 'Create Event')}
        </button>
      </form>
    </div>
  );
};

export default EditEvent;

